// Cookie consent management utilities

export type ConsentPreferences = {
  necessary: boolean; // Always true, can't be disabled
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
};

const CONSENT_KEY = 'cookie_consent';

export const getConsentPreferences = (): ConsentPreferences | null => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const saveConsentPreferences = (preferences: Omit<ConsentPreferences, 'timestamp'>) => {
  const prefs: ConsentPreferences = {
    ...preferences,
    timestamp: Date.now(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
  
  // Initialize or disable analytics based on consent
  if (preferences.analytics) {
    initializeAnalytics();
  } else {
    disableAnalytics();
  }
};

export const hasConsent = (): boolean => {
  return getConsentPreferences() !== null;
};

export const hasAnalyticsConsent = (): boolean => {
  const prefs = getConsentPreferences();
  return prefs?.analytics === true;
};

const initializeAnalytics = () => {
  if (typeof window.gtag !== 'function') {
    // Dynamically load GA4 script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-9W0T1ZH3JK';
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      window.GA_MEASUREMENT_ID = 'G-9W0T1ZH3JK';
      gtag('config', window.GA_MEASUREMENT_ID, {
        anonymize_ip: true,
        cookie_flags: 'SameSite=None;Secure'
      });
    `;
    document.head.appendChild(script2);
  }
};

const disableAnalytics = () => {
  if (typeof window.gtag === 'function') {
    // Disable GA4
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
    });
  }
};

export const clearAllConsent = () => {
  localStorage.removeItem(CONSENT_KEY);
  disableAnalytics();
};

export const respectDoNotTrack = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return navigator.doNotTrack === '1' || 
         (window as any).doNotTrack === '1' || 
         (navigator as any).msDoNotTrack === '1';
};

export const updateAnalyticsConsent = (granted: boolean) => {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
      ad_storage: 'denied',
    });
  }
};
